import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { flushSync } from 'react-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import './DarkModeToggle.css'; // Reusing the same base styles

const LanguageToggle = ({ className = "" }) => {
  const { language, setLanguage } = useLanguage();
  const { isDarkTheme } = useTheme();
  const buttonRef = useRef(null);

  const handleToggle = async () => {
    const newLang = language === 'EN' ? 'ID' : 'EN';
    
    if (
      !document.startViewTransition ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setLanguage(newLang);
      return;
    }

    await document.startViewTransition(() => {
      flushSync(() => {
        setLanguage(newLang);
      });
    }).ready;

    if (!buttonRef.current) return;

    const { top, left, width, height } = buttonRef.current.getBoundingClientRect();
    const x = left + width / 2;
    const y = top + height / 2;
    const right = window.innerWidth - left;
    const bottom = window.innerHeight - top;
    const maxRadius = Math.hypot(
      Math.max(left, right),
      Math.max(top, bottom)
    );

    document.documentElement.animate(
      {
        clipPath: [
          `circle(0px at ${x}px ${y}px)`,
          `circle(${maxRadius}px at ${x}px ${y}px)`,
        ],
      },
      {
        duration: 400,
        easing: "ease-in-out",
        pseudoElement: "::view-transition-new(root)",
      }
    );
  };

  return (
    <motion.button
      ref={buttonRef}
      className={`dark-mode-toggle ${isDarkTheme ? 'is-dark' : ''} ${className}`}
      onClick={handleToggle}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      aria-label="Toggle language"
      style={{
        fontFamily: 'var(--v2-font-mono)',
        fontWeight: 'bold',
        fontSize: '12px',
        textTransform: 'lowercase',
      }}
    >
      <motion.div
        initial={{ rotate: -90, scale: 0 }}
        animate={{ rotate: 0, scale: 1 }}
        transition={{ duration: 0.3 }}
        key={language}
      >
        {language.toLowerCase()}
      </motion.div>
    </motion.button>
  );
};

export default LanguageToggle;
